import type { DarkRoomReview } from "./schemas";
import type { AssessmentEnvelope } from "./assessment";
import { SignalCapsuleReceiptV1Schema, receiptResourceCheck } from "./receiptSchema";

export interface GenreBenchmark {
  genre: string;
  avgReadiness: number;
  sampleSize: number;
  topPersona: string;
  note: string;
}

// No observed, consented and provenance-qualified cohort is available yet.
// Genre labels must not select a fictional population or adjust readiness.
export function resolveGenreBenchmark(_genre: string | undefined): GenreBenchmark | null {
  return null;
}

export type JourneyStep = "landing" | "intake" | "consent" | "dark_room" | "signal" | "release_room" | "package";

export interface JourneyStepMeta {
  id: JourneyStep;
  label: string;
  title: string;
  body: string;
}

export const journeySteps: JourneyStepMeta[] = [
  {
    id: "landing",
    label: "Start",
    title: "Orient",
    body: "Understand what SHADOW will and will not do before sharing creative evidence."
  },
  {
    id: "intake",
    label: "Intake",
    title: "Artist evidence",
    body: "Collect the minimum useful context for a release-readiness read."
  },
  {
    id: "consent",
    label: "Consent",
    title: "Rights and disclosure",
    body: "Make analysis consent, rights attestation, and artificial intelligence disclosure explicit."
  },
  {
    id: "dark_room",
    label: "Dark Room",
    title: "Evaluation",
    body: "Run the protective readiness pass without implying endorsement or publication."
  },
  {
    id: "signal",
    label: "Signal",
    title: "Report",
    body: "Show verdict, persona reads, next actions, and depth recommendation."
  },
  {
    id: "release_room",
    label: "Release Room",
    title: "Gates",
    body: "Turn missing information into concrete readiness milestones."
  },
  {
    id: "package",
    label: "Package",
    title: "Handoff",
    body: "Prepare editable Seamline and Syntha review material without automatic publication."
  }
];

export interface ReleaseMilestone {
  id: string;
  label: string;
  status: "ready" | "open" | "blocked";
  detail: string;
  weight: number;
  intakeField?: "songText" | "links" | "genre" | "goals";
}

export interface ReleaseReadinessMap {
  status: "blocked" | "draft" | "ready_for_review";
  score: number;
  forecastScore: number;
  nextBestMove: string;
  milestones: ReleaseMilestone[];
  completedActions: number;
  totalActions: number;
  completedActionsBonus: number;
  actionsPotentialBonus: number;
  genreBenchmark: GenreBenchmark | null;
}

export interface StudioGateBrief {
  decision: "hold" | "package" | "review";
  label: string;
  riskLevel: "low" | "medium" | "high";
  rationale: string;
  requiredProof: string[];
  escalationPath: string;
  lanes: Array<{
    id: string;
    label: string;
    status: "ready" | "open" | "blocked";
    summary: string;
  }>;
  boundaries: string[];
}

export interface ShadowActionPlanItem {
  id: string;
  title: string;
  urgency: "now" | "next" | "later";
  source: "readiness" | "studio_gate" | "persona" | "package";
  status: "open" | "planned" | "done";
  owner: "artist" | "operator" | "studio";
  closesGate?: string;
  proof: string;
  rationale: string;
  difficulty: 1 | 2 | 3 | 4 | 5;
}

export interface ShadowActionPlan {
  planId: string;
  headline: string;
  nextMove: string;
  items: ShadowActionPlanItem[];
  boundaries: string[];
}

export function buildReadinessMap(review: DarkRoomReview, completedActions = 0, genre?: string): ReleaseReadinessMap {
  const missing = new Set(review.recommendation.missingInfo);
  const validation = new Set(review.synthaHandoff.validationErrors);
  const rightsOpen =
    missing.has("rights_attestation") ||
    missing.has("ai_disclosure_acknowledgement") ||
    validation.has("rights_attestation_required") ||
    validation.has("ai_disclosure_required");
  const evidenceOpen = missing.has("complete_song_or_lyric_evidence");
  const linksOpen = missing.has("public_reference_links");
  const actionProgress = review.recommendation.topActions.length
    ? completedActions / review.recommendation.topActions.length
    : 1;

  const milestones: ReleaseMilestone[] = [
    {
      id: "rights",
      label: "Rights and disclosure",
      status: rightsOpen ? "blocked" : "ready",
      detail: rightsOpen ? "Complete rights attestation and artificial intelligence disclosure." : "Consent primitives are clear enough for draft review.",
      weight: 24
    },
    {
      id: "evidence",
      label: "Creative evidence",
      status: evidenceOpen ? "open" : "ready",
      detail: evidenceOpen ? "Add one complete song, lyric, or performance reference." : "Creative evidence is sufficient for this Signal pass.",
      weight: 18,
      intakeField: "songText" as const
    },
    {
      id: "references",
      label: "Public references",
      status: linksOpen ? "open" : "ready",
      detail: linksOpen ? "Add at least one public reference link." : "Public reference context exists.",
      weight: 12,
      intakeField: "links" as const
    },
    {
      id: "actions",
      label: "Next actions",
      status: actionProgress >= 1 ? "ready" : actionProgress > 0 ? "open" : "open",
      detail: `${completedActions}/${review.recommendation.topActions.length} recommended actions marked done.`,
      weight: 14
    },
    {
      id: "seamline",
      label: "Seamline draft",
      status: review.seamlineDraft.headline ? "ready" : "open",
      detail: "Draft artist page material is available for artist approval.",
      weight: 14
    },
    {
      id: "syntha",
      label: "Syntha review",
      status: review.synthaHandoff.status === "blocked" ? "blocked" : review.synthaHandoff.status === "ready_for_review" ? "ready" : "open",
      detail:
        review.synthaHandoff.status === "blocked"
          ? "Manual Syntha review is blocked until open gates close."
          : "Handoff remains manual and draft-only.",
      weight: 18
    }
  ];

  const score = Math.round(
    milestones.reduce((total, milestone) => {
      if (milestone.status === "ready") return total + milestone.weight;
      if (milestone.status === "open") return total + milestone.weight * 0.45;
      return total;
    }, 0)
  );
  const status = milestones.some((milestone) => milestone.status === "blocked")
    ? "blocked"
    : score >= 86
      ? "ready_for_review"
      : "draft";
  const nextBestMove = milestones.find((milestone) => milestone.status === "blocked" || milestone.status === "open")?.detail ??
    "Prepare the manual Syntha review packet.";

  const totalActions = review.recommendation.topActions.length;
  const actionsWeight = 14;
  const completedActionsBonus = totalActions > 0 ? Math.round(actionProgress * actionsWeight * 0.55) : 0;
  const actionsPotentialBonus = totalActions > 0 ? Math.round((1 - actionProgress) * actionsWeight * 0.55) : 0;
  const genreBenchmark = resolveGenreBenchmark(genre);

  const forecastScore = Math.round(
    milestones.reduce((total, m) => {
      if (m.status !== "blocked") return total + m.weight;
      return total;
    }, 0)
  );

  return { status, score, forecastScore, nextBestMove, milestones, completedActions, totalActions, completedActionsBonus, actionsPotentialBonus, genreBenchmark };
}

export interface ReleasePackageDraft {
  artistId: string;
  reviewId: string;
  headline: string;
  shortBio: string;
  proofItems: string[];
  rightsNotes: string;
  disclosureNotes: string;
  releaseNotes: string;
  handoffStatus: "blocked" | "draft" | "ready_for_review";
  completeness: number;
}

export interface ShadowSignalCapsule {
  assessment?: AssessmentEnvelope;
  capsuleId: string;
  artistName: string;
  generatedAt: string;
  verdict: string;
  suggestedDepth: string;
  confidence: DarkRoomReview["confidence"];
  compositeScores: DarkRoomReview["compositeScores"];
  readiness: {
    status: ReleaseReadinessMap["status"];
    score: number;
    nextBestMove: string;
    openGates: string[];
  };
  nextActions: string[];
  actionPlan: ShadowActionPlan;
  studioGate: StudioGateBrief;
  seamline: {
    headline: string;
    bioShort: string;
  };
  shadowVoiceSummary: string;
  boundaries: string[];
}

export interface ShadowSignalCapsuleReceipt {
  receiptId: string;
  receiptVersion: "shadow-signal-receipt-v1";
  capsule: ShadowSignalCapsule;
  integrity: {
    algorithm: "shadow-stable-fnv1a-64";
    digest: string;
    canonicalLength: number;
  };
  signature: {
    status: "pending_obelisk";
    reason: string;
  };
  importPolicy: {
    allowed: true;
    mode: "artist_owned_continuity";
    boundaries: string[];
  };
}

export interface ShadowReceiptContinuitySummary {
  importStatus: "accepted";
  receiptId: string;
  artistName: string;
  generatedAt: string;
  readinessScore: number;
  readinessStatus: ReleaseReadinessMap["status"];
  suggestedDepth: string;
  openGateCount: number;
  nextActionCount: number;
  signatureStatus: ShadowSignalCapsuleReceipt["signature"]["status"];
  reentryMode: ShadowSignalCapsuleReceipt["importPolicy"]["mode"];
  boundaries: string[];
}

export function buildSignalCapsule(
  review: DarkRoomReview,
  readiness: ReleaseReadinessMap,
  actionStatus: Record<string, ShadowActionPlanItem["status"]> = {}
): ShadowSignalCapsule {
  const openGates = readiness.milestones
    .filter((milestone) => milestone.status !== "ready")
    .map((milestone) => `${milestone.label}: ${milestone.detail}`);
  const studioGate = buildStudioGateBrief(review, readiness);
  const actionPlan = buildShadowActionPlan(review, readiness, studioGate, actionStatus);

  return {
    capsuleId: `capsule_${review.id}`,
    assessment: review.assessment,
    artistName: review.artistName,
    generatedAt: review.assessment?.generatedAt ?? new Date().toISOString(),
    verdict: review.personaReviews.find((persona) => persona.persona === "The Gate")?.verdict ?? "SHADOW Signal generated.",
    suggestedDepth: review.recommendation.suggestedDepth,
    confidence: review.confidence,
    compositeScores: review.compositeScores,
    readiness: {
      status: readiness.status,
      score: readiness.score,
      nextBestMove: readiness.nextBestMove,
      openGates
    },
    nextActions: review.recommendation.topActions,
    actionPlan,
    studioGate,
    seamline: {
      headline: review.seamlineDraft.headline,
      bioShort: review.seamlineDraft.bioShort
    },
    shadowVoiceSummary: buildShadowVoiceSummary(review, readiness),
    boundaries: [
      "SHADOW Signal is guidance, not legal advice or endorsement.",
      "No publication or Syntha submission happens automatically.",
      "Artist creative material is evaluation evidence, not training data."
    ]
  };
}

function deriveActionDifficulty(
  owner: ShadowActionPlanItem["owner"],
  urgency: ShadowActionPlanItem["urgency"],
  closesGate?: string
): ShadowActionPlanItem["difficulty"] {
  let d = owner === "artist" ? 1 : owner === "operator" ? 3 : 4;
  if (closesGate) d += 1;
  if (urgency === "now") d += 1;
  return Math.min(5, d) as ShadowActionPlanItem["difficulty"];
}

export function buildShadowActionPlan(
  review: DarkRoomReview,
  readiness: ReleaseReadinessMap,
  studioGate: StudioGateBrief,
  actionStatus: Record<string, ShadowActionPlanItem["status"]> = {}
): ShadowActionPlan {
  const blockedOrOpen = readiness.milestones.filter((milestone) => milestone.status !== "ready");
  const milestoneItems = blockedOrOpen.map<ShadowActionPlanItem>((milestone) => {
    const urgency = milestone.status === "blocked" ? "now" : "next" as const;
    const closesGate = milestone.label;
    return {
      id: `gate-${milestone.id}`,
      title: milestone.detail,
      urgency,
      source: "readiness",
      status: actionStatus[`gate-${milestone.id}`] ?? "open",
      owner: "artist",
      closesGate,
      proof: milestone.id === "rights" ? "Rights attestation and artificial intelligence disclosure." : milestone.detail,
      rationale:
        milestone.status === "blocked"
          ? "This gate blocks safe escalation until the proof exists."
          : "This proof raises release readiness and reduces manual review friction.",
      difficulty: deriveActionDifficulty("artist", urgency, closesGate)
    };
  });

  const recommendedItems = review.recommendation.topActions.map<ShadowActionPlanItem>((action, index) => {
    const urgency = index === 0 && !milestoneItems.some((item) => item.urgency === "now") ? "now" : "next" as const;
    return {
      id: `action-${index + 1}`,
      title: action,
      urgency,
      source: "persona",
      status: actionStatus[action] ?? "open",
      owner: "artist",
      proof: "Attach the revised artifact, reference, or decision note before asking for review.",
      rationale: "This came from the Dark Room persona read and should improve the next Signal pass.",
      difficulty: deriveActionDifficulty("artist", urgency)
    };
  });

  const gateOwner = studioGate.decision === "review" ? "operator" : "artist" as const;
  const gateUrgency = studioGate.decision === "hold" ? "later" : "next" as const;
  const gateItem: ShadowActionPlanItem = {
    id: "studio-gate-package",
    title: studioGate.escalationPath,
    urgency: gateUrgency,
    source: "studio_gate",
    status: actionStatus["studio-gate-package"] ?? "open",
    owner: gateOwner,
    closesGate: "Studio Gate",
    proof: studioGate.requiredProof.join(" "),
    rationale: studioGate.rationale,
    difficulty: deriveActionDifficulty(gateOwner, gateUrgency, "Studio Gate")
  };

  const packageUrgency = readiness.status === "blocked" ? "later" : "next" as const;
  const packageItem: ShadowActionPlanItem = {
    id: "release-package-proof",
    title: "Assemble the Release Room package with proof items, rights notes, disclosure notes, and Seamline copy.",
    urgency: packageUrgency,
    source: "package",
    status: actionStatus["release-package-proof"] ?? "open",
    owner: "artist",
    closesGate: "Release package",
    proof: "Release package draft, proof items, rights notes, disclosure notes, and manual-review status.",
    rationale: "A complete package reduces operator guesswork before any Syntha or Studio Gate escalation.",
    difficulty: deriveActionDifficulty("artist", packageUrgency, "Release package")
  };

  const items = [...milestoneItems, ...recommendedItems, gateItem, packageItem]
    .filter((item, index, all) => all.findIndex((candidate) => candidate.title === item.title) === index)
    .sort((left, right) => urgencyRank(left.urgency) - urgencyRank(right.urgency))
    .slice(0, 8);

  return {
    planId: `action_plan_${review.id}`,
    headline:
      readiness.status === "blocked"
        ? "Close blocked proof gates before escalation."
        : readiness.status === "ready_for_review"
          ? "Package proof for manual review."
          : "Turn Signal into a stronger Release Room package.",
    nextMove: items[0]?.title ?? readiness.nextBestMove,
    items,
    boundaries: [
      "The action plan is guidance, not legal advice or endorsement.",
      "No item publishes music, submits to Syntha, or grants Studio Gate approval automatically.",
      "Artist-owned proof should stay attached to any future review packet."
    ]
  };
}

export function buildStudioGateBrief(review: DarkRoomReview, readiness: ReleaseReadinessMap): StudioGateBrief {
  const blocked = readiness.milestones.filter((milestone) => milestone.status === "blocked");
  const open = readiness.milestones.filter((milestone) => milestone.status === "open");
  const proofMilestones = [...blocked, ...open];
  const missingProof = proofMilestones.map((milestone) => `${milestone.label}: ${milestone.detail}`);
  const lowConfidence = review.confidence === "low";
  const highRisk = blocked.length > 0 || lowConfidence || review.recommendation.escalationFlags.length > 1;
  const decision =
    blocked.length > 0 || lowConfidence
      ? "hold"
      : readiness.status === "ready_for_review" && review.compositeScores.overall >= 72
        ? "review"
        : "package";
  const label =
    decision === "hold"
      ? "Hold escalation"
      : decision === "review"
        ? "Manual Studio Gate review"
        : "Package the proof";
  const riskLevel = highRisk ? "high" : decision === "review" ? "low" : "medium";
  const rationale =
    decision === "hold"
      ? "Open gates or low confidence make escalation unsafe until the artist supplies proof."
      : decision === "review"
        ? "Core gates are ready enough for manual review, but SHADOW still does not approve publication automatically."
        : "The artist can assemble a stronger Release Room package before any Studio Gate review.";

  return {
    decision,
    label,
    riskLevel,
    rationale,
    requiredProof: missingProof.length ? missingProof : ["No extra proof required before manual review; keep evidence attached."],
    escalationPath:
      decision === "hold"
        ? "Return to Release Room and close blocked gates before Syntha or VaultSpark endorsement review."
        : decision === "review"
          ? "Prepare a manual Studio Gate review packet with receipt, Release Room package, and artist confirmation."
          : "Package Seamline copy, proof items, rights notes, and disclosure notes for operator review.",
    lanes: [
      {
        id: "rights",
        label: "Rights lane",
        status: readiness.milestones.find((milestone) => milestone.id === "rights")?.status ?? "open",
        summary: readiness.milestones.find((milestone) => milestone.id === "rights")?.detail ?? "Rights posture unavailable."
      },
      {
        id: "evidence",
        label: "Proof lane",
        status: readiness.milestones.find((milestone) => milestone.id === "evidence")?.status ?? "open",
        summary: readiness.milestones.find((milestone) => milestone.id === "evidence")?.detail ?? "Evidence posture unavailable."
      },
      {
        id: "syntha",
        label: "Syntha lane",
        status: readiness.milestones.find((milestone) => milestone.id === "syntha")?.status ?? "open",
        summary: review.synthaHandoff.status === "blocked"
          ? "Syntha handoff is blocked."
          : "Syntha handoff remains draft-only and manual."
      }
    ],
    boundaries: [
      "Studio Gate is a risk posture, not an endorsement.",
      "No publication, distribution, or Syntha submission happens automatically.",
      "Legal and rights questions still require professional review."
    ]
  };
}

export function buildSignalCapsuleReceipt(capsule: ShadowSignalCapsule): ShadowSignalCapsuleReceipt {
  const canonical = canonicalJson(capsule);
  const digest = stableHash64(canonical);

  return {
    receiptId: `receipt_${capsule.capsuleId}_${digest.slice(0, 12)}`,
    receiptVersion: "shadow-signal-receipt-v1",
    capsule,
    integrity: {
      algorithm: "shadow-stable-fnv1a-64",
      digest,
      canonicalLength: canonical.length
    },
    signature: {
      status: "pending_obelisk",
      reason: "Cryptographic signing waits for Obelisk session claims; this receipt is an integrity-checked artist-owned import package."
    },
    importPolicy: {
      allowed: true,
      mode: "artist_owned_continuity",
      boundaries: [
        "Receipt supports a local SHADOW Signal context preview only.",
        "Import does not prove identity, rights ownership, or publication approval.",
        "Obelisk signing is required before this receipt can become an authenticated account artifact."
      ]
    }
  };
}

export function validateSignalCapsuleReceipt(input: unknown): { valid: boolean; reason: string } {
  try { return validateReceiptInner(input); }
  catch { return { valid: false, reason: "malformed_receipt" }; }
}

function validateReceiptInner(input: unknown): { valid: boolean; reason: string } {
  const limitReason = receiptResourceCheck(input);
  if (limitReason) return { valid: false, reason: limitReason };
  if (!input || typeof input !== "object" || Array.isArray(input)) return { valid: false, reason: "malformed_receipt" };
  const receipt = input as Partial<ShadowSignalCapsuleReceipt>;
  if (!receipt.capsule || !receipt.integrity || !receipt.signature || !receipt.importPolicy) {
    return { valid: false, reason: "malformed_receipt" };
  }
  if (receipt.receiptVersion !== "shadow-signal-receipt-v1") {
    return { valid: false, reason: "unsupported_receipt_version" };
  }
  if (receipt.integrity.algorithm !== "shadow-stable-fnv1a-64") {
    return { valid: false, reason: "unsupported_integrity_algorithm" };
  }

  if (!SignalCapsuleReceiptV1Schema.safeParse(receipt).success) return { valid: false, reason: "malformed_receipt" };

  const canonical = canonicalJson(receipt.capsule);
  if (canonical.length !== receipt.integrity.canonicalLength) {
    return { valid: false, reason: "canonical_length_mismatch" };
  }

  const digest = stableHash64(canonical);
  if (digest !== receipt.integrity.digest) {
    return { valid: false, reason: "digest_mismatch" };
  }

  return { valid: true, reason: "receipt_integrity_ok" };
}

export function summarizeSignalReceipt(receipt: ShadowSignalCapsuleReceipt): ShadowReceiptContinuitySummary {
  const validation = validateSignalCapsuleReceipt(receipt);
  if (!validation.valid) {
    throw new Error(validation.reason);
  }

  return {
    importStatus: "accepted",
    receiptId: receipt.receiptId,
    artistName: receipt.capsule.artistName,
    generatedAt: receipt.capsule.generatedAt,
    readinessScore: receipt.capsule.readiness.score,
    readinessStatus: receipt.capsule.readiness.status,
    suggestedDepth: receipt.capsule.suggestedDepth,
    openGateCount: receipt.capsule.readiness.openGates.length,
    nextActionCount: receipt.capsule.nextActions.length,
    signatureStatus: receipt.signature.status,
    reentryMode: receipt.importPolicy.mode,
    boundaries: ["Unsigned artist-owned context only; checksum does not authenticate the artist.", "Import does not prove identity, rights ownership, or publication approval.", "Import cannot authorize rights, accounts, payments or publication."]
  };
}

export function buildReleasePackageDraft(review: DarkRoomReview, readiness: ReleaseReadinessMap): ReleasePackageDraft {
  const proofItems = [
    ...review.seamlineDraft.proofItems,
    ...review.personaReviews.slice(0, 2).map((persona) => `${persona.persona}: ${persona.action}`)
  ].slice(0, 5);
  const rightsBlocked = readiness.milestones.find((milestone) => milestone.id === "rights")?.status !== "ready";
  const completeness = Math.min(100, Math.round((readiness.score * 0.72) + (proofItems.length * 4) + (rightsBlocked ? 0 : 8)));

  return {
    artistId: review.artistId,
    reviewId: review.id,
    headline: review.seamlineDraft.headline,
    shortBio: review.seamlineDraft.bioShort,
    proofItems,
    rightsNotes: rightsBlocked ? "Rights/disclosure proof required before escalation." : "Rights and disclosure basics accepted for draft review.",
    disclosureNotes: "Artificial intelligence involvement must remain explicit anywhere SHADOW guidance is used publicly.",
    releaseNotes: readiness.nextBestMove,
    handoffStatus: readiness.status,
    completeness
  };
}

export type ShadowAnalyticsEventName =
  | "shadow_landing_view"
  | "shadow_signal_started"
  | "shadow_signal_completed"
  | "dark_room_lite_generated"
  | "signal_capsule_exported"
  | "signal_receipt_emailed"
  | "signal_receipt_imported"
  | "release_room_opened"
  | "seamline_draft_created"
  | "syntha_handoff_created"
  | "depth_recommendation_viewed"
  | "upgrade_clicked"
  | "artist_flagged_high_potential"
  | "artist_flagged_high_risk"
  | "consent_required_block"
  | "signal_feedback_submitted"
  | "benchmark_opted_in";

export interface ShadowAnalyticsEvent {
  eventName: ShadowAnalyticsEventName;
  artistId?: string;
  reviewId?: string;
  route?: string;
  metadata?: Record<string, string | number | boolean | undefined>;
  createdAt: string;
}

export function buildAnalyticsEvent(
  eventName: ShadowAnalyticsEventName,
  input: Omit<ShadowAnalyticsEvent, "eventName" | "createdAt"> = {}
): ShadowAnalyticsEvent {
  return {
    eventName,
    ...input,
    createdAt: new Date().toISOString()
  };
}

export interface DepthTier {
  id: "Trace" | "Echo" | "Mirror" | "Twin" | "SHADOW";
  label: string;
  fit: string;
  boundary: string;
}

export const depthTiers: DepthTier[] = [
  {
    id: "Trace",
    label: "Trace",
    fit: "Deeper release-readiness review and tighter next-action plan.",
    boundary: "Instant checkout is not live until Stripe, refund, and legal gates clear."
  },
  {
    id: "Echo",
    label: "Echo",
    fit: "Broader identity, catalog, and campaign read for artists with stronger evidence.",
    boundary: "No endorsement or publication is implied."
  },
  {
    id: "Mirror",
    label: "Mirror",
    fit: "Catalog-aware review after explicit private-material consent.",
    boundary: "Private catalog work requires paid/private consent."
  },
  {
    id: "Twin",
    label: "Twin",
    fit: "High-touch operating support for qualified artists.",
    boundary: "Application or manual approval required."
  },
  {
    id: "SHADOW",
    label: "SHADOW",
    fit: "Full artist operating layer with bespoke review and studio gates.",
    boundary: "Founder/studio approval required."
  }
];

export function recommendedDepthIndex(depth: string): number {
  const index = depthTiers.findIndex((tier) => tier.id === depth);
  return index === -1 ? 0 : index;
}

function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.entries(value)
      .filter(([, entry]) => entry !== undefined)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, entry]) => `${JSON.stringify(key)}:${canonicalJson(entry)}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
}

function stableHash64(input: string): string {
  let hash = 0xcbf29ce484222325n;
  const prime = 0x100000001b3n;

  for (let i = 0; i < input.length; i += 1) {
    hash ^= BigInt(input.charCodeAt(i));
    hash = BigInt.asUintN(64, hash * prime);
  }

  return hash.toString(16).padStart(16, "0");
}

function urgencyRank(urgency: ShadowActionPlanItem["urgency"]): number {
  if (urgency === "now") return 0;
  if (urgency === "next") return 1;
  return 2;
}

export interface PersonaTension {
  type: "consensus_risk" | "consensus_strength" | "divergence";
  personaA: string;
  personaB: string;
  label: string;
}

const AXIS_VOICE: Record<string, { strength: string; weakness: string }> = {
  music: {
    strength: "The sonic dimension is carrying you — it's specific, and specificity is what a room remembers.",
    weakness: "The music dimension is the anchor dragging the composite down — that's where the proof needs to land first.",
  },
  identity: {
    strength: "Your artistic identity is your clearest asset — there's a there there, and it reads distinctly.",
    weakness: "Identity is the open question — without a clear lane, every other dimension has nothing to attach to.",
  },
  market: {
    strength: "The market positioning is working — you've built enough context that a pitch conversation is realistic.",
    weakness: "Market fit is the exposed flank — the music may be real, but the room can't place you yet.",
  },
  readiness: {
    strength: "The infrastructure is in order — rights, links, and catalog signals are clean enough to move.",
    weakness: "Readiness gates are blocking the signal from landing — fix the infrastructure before doing anything else.",
  },
  catalog: {
    strength: "Catalog depth is showing — you've given SHADOW enough material to evaluate with real specificity.",
    weakness: "Catalog is thin — without documented releases and supporting material, the evaluation can only go so deep.",
  },
  studioFit: {
    strength: "Studio fit reads as strong — the profile aligns with what a release conversation would require.",
    weakness: "Studio fit is the weakest link — the gap between current signal and review-ready needs to close here.",
  },
};

export function buildShadowVoiceSummary(review: DarkRoomReview, readiness: ReleaseReadinessMap): string {
  if (review.assessment?.mode !== "human_review") {
    const method = review.assessment?.mode.replaceAll("_", " ") ?? "unknown legacy method";
    return `I'm organizing supplied preparation evidence using ${method}. ${readiness.milestones.filter(m => m.status !== "ready").length} checklist dependencies remain unresolved; musical quality, audience demand and legal clearance are not established. Priority: ${review.recommendation.topActions[0] ?? "Clarify the missing evidence before further review"}`;
  }
  const band = readiness.score < 40 ? "early" : readiness.score < 65 ? "developing" : readiness.score < 80 ? "near" : "primed";
  const sorted = [...review.personaReviews].sort((a, b) => a.score - b.score);
  const weakest = sorted[0];
  const strongest = sorted[sorted.length - 1];
  const topAction = review.recommendation.topActions[0] ?? "Address the open readiness gates before the next submission cycle";

  const openings: Record<string, string> = {
    early: "I'm seeing Signal at an early stage — real material is here, but critical gates need clearing before any release conversation begins.",
    developing: "Your Signal is developing — there's genuine substance, and the gap between where you are and review-ready is specific and closeable.",
    near: "You're positioned close to review-ready — the remaining friction is targeted and resolvable with direct action.",
    primed: "Your Signal is primed — the critical gates are clear and the work speaks clearly for itself at this stage."
  };

  const scores = review.compositeScores as Record<string, number>;
  const axisScores = Object.entries(scores).filter(([k]) => k !== "overall" && scores[k] !== undefined);
  const sortedAxes = axisScores.sort(([, a], [, b]) => b - a);
  const topAxis = sortedAxes[0]?.[0];
  const bottomAxis = sortedAxes[sortedAxes.length - 1]?.[0];

  const axisLine = topAxis && bottomAxis && topAxis !== bottomAxis
    ? ` ${AXIS_VOICE[topAxis]?.strength ?? ""} ${AXIS_VOICE[bottomAxis]?.weakness ?? ""}`
    : ` ${strongest.persona} reads as your strongest signal at ${Math.round(strongest.score)} — that's the case you lead with. ${weakest.persona} sits at ${Math.round(weakest.score)}, and that's the first thing a room will push on.`;

  const s1 = openings[band];
  const action = topAction.charAt(0).toLowerCase() + topAction.slice(1).replace(/\.$/, "");
  const s3 = `Priority: ${action} — move that and your composite position shifts.`;

  return `${s1}${axisLine} ${s3}`;
}

export interface ReleaseWindowSuggestion {
  weeksToReady: number | null;
  windowLabel: string;
  openGateCount: number;
  topBlocker: string;
  confidence: "low" | "medium" | "high";
}

export function buildReleaseWindowSuggestion(map: ReleaseReadinessMap): ReleaseWindowSuggestion {
  const open = map.milestones.filter(m => m.status !== "ready");
  return { weeksToReady: null, windowLabel: "Schedule not estimated", openGateCount: open.length,
    topBlocker: open.find(m => m.status === "blocked")?.label ?? open[0]?.label ?? "Independent review remains required",
    confidence: "low" };
}

export const PERSONA_AXIS_MAP: Record<string, string> = {
  "The Listener": "music",
  "The Ghost": "identity",
  "The Broker": "readiness",
  "The Scribe": "catalog",
  "The Vessel": "identity",
  "The Architect": "studioFit",
  "The Pulse": "market",
  "The Heretic": "overall",
  "The Hunter": "market",
  "The Gate": "overall"
};

export interface PersonaRecoveryPath {
  why: string;
  steps: string[];
  timeframe: string;
}

export const PERSONA_RECOVERY_PATHS: Record<string, PersonaRecoveryPath> = {
  "The Listener": {
    why: "Your music's emotional clarity is the gateway for new listeners. Low score means the sonic story isn't landing.",
    steps: [
      "Record a stripped-back version of your strongest track — no production, just voice and melody.",
      "Share it in 3 communities and ask: 'What does this make you feel?' Collect 10 responses.",
      "Identify the 2–3 emotion words that appear most. Inject that language into your bio and social hooks."
    ],
    timeframe: "2–3 weeks"
  },
  "The Ghost": {
    why: "Identity ambiguity makes you forgettable. A score below 60 means your visual and verbal story aren't aligned.",
    steps: [
      "Write a one-paragraph artist statement in first person — what you make, who it's for, and why it matters.",
      "Audit your top 3 social profiles against that statement. Fix misaligned bios in one sitting.",
      "Shoot or commission a signature press photo that matches the statement's tone."
    ],
    timeframe: "1–2 weeks"
  },
  "The Broker": {
    why: "Release readiness is a checklist. Low score means critical infrastructure is missing before you can safely release.",
    steps: [
      "Complete your publishing registration with ASCAP, BMI, or equivalent.",
      "Resolve all split sheets and rights documentation for any collaborative tracks.",
      "Set up your distributor account and verify your artist pages on major platforms."
    ],
    timeframe: "2–4 weeks"
  },
  "The Scribe": {
    why: "Catalog depth signals staying power. A thin catalog means you can't sustain discovery-driven momentum.",
    steps: [
      "Identify your 3 unreleased tracks closest to completion — finish the one that's 80% there.",
      "Set a weekly production block of at least 4 hours. Protect it like a meeting.",
      "Aim for 3 releasable tracks within 60 days before pushing for placement or press."
    ],
    timeframe: "4–8 weeks"
  },
  "The Vessel": {
    why: "Authenticity coherence is the delta between who you say you are and what the work actually delivers.",
    steps: [
      "Compare your artist statement against your three most recent releases. List every contradiction.",
      "Pick one contradiction and resolve it — either update the statement or refine the work.",
      "Ask a trusted listener if the music and the brand feel like they come from the same artist."
    ],
    timeframe: "2–3 weeks"
  },
  "The Architect": {
    why: "Studio-fit readiness determines whether you can survive a label, sync, or publishing relationship.",
    steps: [
      "Read one sync licensing deal or label deal (publicly available) to understand what they actually want.",
      "Identify your production weak points: mixing, mastering, or arrangement. Target one per month.",
      "Build a 3-track pitch package with stems, instrumentals, and a one-sheet."
    ],
    timeframe: "4–6 weeks"
  },
  "The Pulse": {
    why: "Market awareness separates artists who get placed from those who get passed on.",
    steps: [
      "Map your top 3 genre peers who are actively releasing. Note what's working for them.",
      "Find 2 playlist curators or sync supervisors actively seeking your genre. Reach out with a tailored pitch.",
      "Track your Spotify for Artists data weekly — focus on save rate, not just plays."
    ],
    timeframe: "Ongoing — 3-week sprint to baseline"
  },
  "The Heretic": {
    why: "Overall cohesion score reflects how well all axes align. Low score means isolated strengths that don't add up.",
    steps: [
      "Run a gap audit: list your highest and lowest scoring personas. The gap IS the problem.",
      "Focus all energy on closing the lowest scoring axis — not polishing what's already strong.",
      "Reassess after 3 weeks of targeted work on the lowest gap."
    ],
    timeframe: "3–6 weeks"
  },
  "The Hunter": {
    why: "Opportunity capture is about timing and positioning. Low score means you're not ready to say yes when the moment arrives.",
    steps: [
      "Identify 5 concrete opportunities in your lane: open calls, submissions, sync briefs, or live slots.",
      "Prepare a 1-page pitch document and a 3-track streaming link ready to share in under 2 minutes.",
      "Apply to 3 opportunities this week — speed is the point."
    ],
    timeframe: "1–2 weeks"
  },
  "The Gate": {
    why: "SHADOW's final gate check covers overall readiness across all risk vectors. A low Gate score means structural blockers remain.",
    steps: [
      "Address every open gate in the Studio Gate section — rights, identity, and catalog first.",
      "Work through SHADOW's ranked action plan from top to bottom without skipping.",
      "Return for a second Signal run after closing at least 3 gates."
    ],
    timeframe: "2–4 weeks"
  }
};

export function computePersonaGenreDeltas(
  personaReviews: ReadonlyArray<{ persona: string; score: number }>,
  genreBenchmark: GenreBenchmark | null
): Record<string, number> {
  if (!genreBenchmark) return {};
  const result: Record<string, number> = {};
  for (const pr of personaReviews) {
    result[pr.persona] = Math.round(pr.score - genreBenchmark.avgReadiness);
  }
  return result;
}

export function computePersonaTensions(
  personaReviews: ReadonlyArray<{ persona: string; score: number }>
): PersonaTension[] {
  if (personaReviews.length < 2) return [];
  const sorted = [...personaReviews].sort((a, b) => a.score - b.score);
  const median = sorted[Math.floor(sorted.length / 2)].score;
  const tensions: PersonaTension[] = [];

  const lowest = sorted.slice(0, 2);
  if (lowest.every((p) => p.score < median)) {
    tensions.push({
      type: "consensus_risk",
      personaA: lowest[0].persona,
      personaB: lowest[1].persona,
      label: `${lowest[0].persona} + ${lowest[1].persona} aligned on risk`
    });
  }

  const highest = sorted.slice(-2);
  if (highest.every((p) => p.score > median)) {
    tensions.push({
      type: "consensus_strength",
      personaA: highest[0].persona,
      personaB: highest[1].persona,
      label: `${highest[0].persona} + ${highest[1].persona} aligned on strength`
    });
  }

  const delta = sorted[sorted.length - 1].score - sorted[0].score;
  if (delta >= 25) {
    tensions.push({
      type: "divergence",
      personaA: sorted[0].persona,
      personaB: sorted[sorted.length - 1].persona,
      label: `${sorted[sorted.length - 1].persona} vs ${sorted[0].persona}: +${Math.round(delta)} pt spread`
    });
  }

  return tensions;
}

export function buildSignalDigestDraft(
  review: DarkRoomReview,
  readiness: ReleaseReadinessMap,
  seamlineHeadline: string,
  seamlineBio: string
): string {
  const topPersonas = [...review.personaReviews]
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map((p) => `${p.persona} (${Math.round(p.score)})`);
  const openGates = readiness.milestones.filter((m) => m.status !== "ready").slice(0, 3);
  const topAction = review.recommendation.topActions[0] ?? "Address open gates before next submission";

  const lines = [
    `Subject: ${review.artistName} — Release Readiness Signal`,
    "",
    "Hi,",
    "",
    `I'm sharing a quick signal read on ${review.artistName} from SHADOW.`,
    `Method: ${review.assessment?.mode ?? "unknown legacy method"}; rubric: ${review.assessment?.rubricVersion ?? "unknown"}; generated: ${review.assessment?.generatedAt ?? "unknown"}.`,
    "No audio or linked performance was examined; ownership and audience demand are not verified.",
    `Missing evidence: ${review.assessment?.missingEvidence?.join(", ") || (review.assessment?.missingEvidence ? "No missing fields in this rubric; independent verification still required" : "Unknown in this legacy assessment")}.`,
    "",
    `Headline: ${seamlineHeadline}`,
    "",
    `Bio: ${seamlineBio}`,
    "",
    `Readiness score: ${Math.round(readiness.score)}/100`,
    `Status: ${readiness.status.replaceAll("_", " ")}`,
    "",
    `Strongest perspectives: ${topPersonas.join(" · ")}`,
    ""
  ];

  if (openGates.length > 0) {
    lines.push(`Open gates (${openGates.length}):`);
    for (const gate of openGates) lines.push(`  · ${gate.label}`);
    lines.push("");
  }

  lines.push(`Priority next step: ${topAction}`);
  lines.push("");
  lines.push("—");
  lines.push("This signal was generated by SHADOW. It reflects readiness analysis only, not endorsement or publication.");

  return lines.join("\n");
}

export interface ReadinessVelocity {
  trend: "rising" | "falling" | "flat";
  deltaPerSession: number;
  sessions: number;
  label: string;
}

export function computeReadinessVelocity(
  history: Array<{ readinessScore: number }>
): ReadinessVelocity | null {
  if (history.length < 2) return null;
  const recent = history.slice(-5);
  const deltas = recent.slice(1).map((e, i) => e.readinessScore - recent[i].readinessScore);
  const avg = deltas.reduce((s, d) => s + d, 0) / deltas.length;
  const trend: ReadinessVelocity["trend"] = avg > 1 ? "rising" : avg < -1 ? "falling" : "flat";
  const sign = avg > 0 ? "+" : "";
  const label = trend === "rising"
    ? `↑ +${Math.round(avg)} pts/session`
    : trend === "falling"
    ? `↓ ${Math.round(avg)} pts/session`
    : "→ Holding steady";
  return { trend, deltaPerSession: Math.round(avg * 10) / 10, sessions: recent.length, label };
}

export function computePersistentBlockers(
  history: Array<{ openGates?: string[] }>,
  minSessions = 2
): Array<{ gate: string; sessions: number }> {
  if (history.length < minSessions) return [];
  const gateCount: Record<string, number> = {};
  for (const entry of history) {
    for (const gate of entry.openGates ?? []) {
      gateCount[gate] = (gateCount[gate] ?? 0) + 1;
    }
  }
  return Object.entries(gateCount)
    .filter(([, count]) => count >= minSessions)
    .sort(([, a], [, b]) => b - a)
    .map(([gate, sessions]) => ({ gate, sessions }));
}

// A50-1: Persona reaction lines — persona-keyed responses to score delta vs prior session
const PERSONA_REACTION_LINES: Record<string, { improved: string; declined: string; stable: string }> = {
  "The Listener": {
    improved: "Something shifted. The room feels different — I'm hearing more signal this time.",
    declined: "I was expecting more. The sonic space contracted since I last read you.",
    stable: "Consistent. What you gave me last time is exactly what you gave me now."
  },
  "The Ghost": {
    improved: "The identity sharpened. Whatever you moved, it's landing with more intention.",
    declined: "The shadow is lighter now — not necessarily stronger. Something diffused.",
    stable: "Same presence, same restraint. The mystery hasn't shifted in either direction."
  },
  "The Broker": {
    improved: "The infrastructure is cleaner. I'm seeing fewer open lines, more closed doors.",
    declined: "Something opened that was closed before. The floor got looser.",
    stable: "No movement on the gates. The position held, neither forward nor back."
  },
  "The Scribe": {
    improved: "More pages this time. The writing is earning more trust from me.",
    declined: "The written signal thinned. I have less to evaluate than before.",
    stable: "The lyric evidence is where it was. No regression, no new material."
  },
  "The Vessel": {
    improved: "The performance grounding strengthened. The body in the work is more readable.",
    declined: "The vessel feels emptier now. Something moved away from the stage.",
    stable: "The performance evidence held. Not deeper, not further — the same read."
  },
  "The Architect": {
    improved: "The plan got more load-bearing. I can see more structure under it now.",
    declined: "The scaffolding weakened. Some joint I relied on is no longer there.",
    stable: "The release structure is holding at the same grade. No new floors built."
  },
  "The Pulse": {
    improved: "The market frequency is stronger. I'm picking up a cleaner signal this time.",
    declined: "The audience signal got quieter. The market context lost definition.",
    stable: "The market read is unchanged. Same frequency, same reach, same gaps."
  },
  "The Heretic": {
    improved: "The risks got smaller — or the strengths outweighed them more clearly.",
    declined: "The gap widened. What I was skeptical of before confirmed itself.",
    stable: "Nothing changed my position. The critique stands as I left it."
  },
  "The Hunter": {
    improved: "The positioning tightened. There are more openings to move through now.",
    declined: "The window narrowed. Something in the opportunity posture stepped back.",
    stable: "The hunt is the same one. No new leads, no lost leads."
  },
  "The Gate": {
    improved: "More gates closed. The overall picture is cleaner than my last pass.",
    declined: "More gates open now. The overall readiness posture stepped back.",
    stable: "The same gates, the same state. The picture did not move."
  }
};

export function getPersonaReactionLine(personaName: string, scoreDelta: number): string {
  const lines = PERSONA_REACTION_LINES[personaName];
  if (!lines) return "";
  if (scoreDelta > 3) return lines.improved;
  if (scoreDelta < -3) return lines.declined;
  return lines.stable;
}

export interface ArtistArchetypeProfile {
  archetype: string;
  tagline: string;
  strength: string;
  growthEdge: string;
  nextMove: string;
}

const ARCHETYPE_DEFINITIONS: Array<{
  id: string;
  archetype: string;
  tagline: string;
  strength: string;
  growthEdge: string;
  nextMove: string;
  match: (scores: Record<string, number>) => boolean;
}> = [
  {
    id: "underground_visionary",
    archetype: "The Underground Visionary",
    tagline: "You have the soul. Now build the infrastructure.",
    strength: "Distinct artistic identity and sonic clarity that cuts through noise.",
    growthEdge: "Market positioning and catalog documentation are holding you back from the release you deserve.",
    nextMove: "Secure your rights paperwork and build a proper link stack before the next submission window.",
    match: (s) => (s.identity ?? 0) >= 70 && (s.music ?? 0) >= 65 && (s.market ?? 0) < 65,
  },
  {
    id: "market_operator",
    archetype: "The Market-Ready Operator",
    tagline: "You know the game. Now deepen the voice.",
    strength: "You understand the release process, the platforms, and how to move — better than most.",
    growthEdge: "The craft dimension needs to catch up to the strategy. Your sound needs a stronger identity anchor.",
    nextMove: "Focus one project entirely on artistic exploration before the next commercial push.",
    match: (s) => (s.market ?? 0) >= 72 && (s.readiness ?? 0) >= 68 && (s.identity ?? 0) < 68,
  },
  {
    id: "ghost_in_machine",
    archetype: "The Ghost in the Machine",
    tagline: "The work is real. The world just can't find you yet.",
    strength: "Compositional depth and a unique perspective that persona evaluators consistently notice.",
    growthEdge: "Catalog visibility, link infrastructure, and rights documentation are near-zero — the music is invisible.",
    nextMove: "Spend a week on paperwork and platform presence before any release attempt.",
    match: (s) => (s.music ?? 0) >= 65 && (s.catalog ?? 0) < 55,
  },
  {
    id: "rising_wave",
    archetype: "The Rising Wave",
    tagline: "Momentum is real. This is the moment to push.",
    strength: "Balanced across dimensions with visible upward trajectory — a rare profile for this stage.",
    growthEdge: "You're close enough that small, targeted improvements will break the threshold fast.",
    nextMove: "Pick the one open gate with the easiest close and move immediately.",
    match: (s) => (s.overall ?? 0) >= 65 && (s.overall ?? 0) < 80,
  },
  {
    id: "veteran_ready",
    archetype: "The Veteran Ready",
    tagline: "You've done the work. SHADOW sees it.",
    strength: "Near-complete readiness across music, identity, market, and catalog. Release is within reach.",
    growthEdge: "The remaining gaps are execution details, not foundational weaknesses.",
    nextMove: "Close the final open gates and schedule the release window in the next 4–6 weeks.",
    match: (s) => (s.overall ?? 0) >= 80,
  },
  {
    id: "uncharted",
    archetype: "The Uncharted Artist",
    tagline: "Something is forming here that doesn't fit the standard map.",
    strength: "Unusual signal patterns suggest originality that conventional metrics can't fully capture.",
    growthEdge: "Build enough infrastructure to give the art a fair shot at being heard.",
    nextMove: "Start with rights and links — the fundamentals that no archetype can skip.",
    match: () => true,
  },
];

export interface ScoreAnatomySegment {
  label: string;
  score: number;
  weight: number;
  contribution: number;
  color: string;
}

export interface ScoreAnatomy {
  total: number;
  segments: ScoreAnatomySegment[];
  dominantAxis: string;
  weakestAxis: string;
}

const SCORE_AXIS_CONFIG: Array<{ key: string; label: string; weight: number; color: string }> = [
  { key: "music", label: "Music", weight: 0.25, color: "#7c5cbf" },
  { key: "identity", label: "Identity", weight: 0.2, color: "#2980b9" },
  { key: "market", label: "Market", weight: 0.2, color: "#16a085" },
  { key: "readiness", label: "Readiness", weight: 0.2, color: "#d35400" },
  { key: "catalog", label: "Catalog", weight: 0.1, color: "#8e44ad" },
  { key: "studioFit", label: "Studio Fit", weight: 0.05, color: "#c0392b" },
];

export function computeScoreAnatomy(review: DarkRoomReview): ScoreAnatomy {
  const scores = review.compositeScores as Record<string, number>;
  const segments: ScoreAnatomySegment[] = SCORE_AXIS_CONFIG
    .filter((ax) => scores[ax.key] !== undefined)
    .map((ax) => ({
      label: ax.label,
      score: scores[ax.key],
      weight: ax.weight,
      contribution: Math.round(scores[ax.key] * ax.weight),
      color: ax.color,
    }));

  const sorted = [...segments].sort((a, b) => b.score - a.score);
  return {
    total: review.compositeScores.overall,
    segments,
    dominantAxis: sorted[0]?.label ?? "—",
    weakestAxis: sorted[sorted.length - 1]?.label ?? "—",
  };
}

export interface AxisConsensusRow {
  axis: string;
  personas: string[];
  avgScore: number;
  agreement: "split" | "moderate" | "strong";
  heatLevel: number;
}

const CANONICAL_AXES = ["music", "identity", "market", "readiness", "catalog"] as const;

export function buildAxisConsensusMatrix(review: DarkRoomReview): AxisConsensusRow[] {
  const scoreByPersona: Record<string, number> = {};
  for (const pr of review.personaReviews) scoreByPersona[pr.persona] = pr.score;

  return CANONICAL_AXES.map((axis) => {
    const personas = Object.entries(PERSONA_AXIS_MAP)
      .filter(([, a]) => a === axis)
      .map(([p]) => p);
    const scores = personas.map((p) => scoreByPersona[p] ?? 0).filter((s) => s > 0);
    const avgScore = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
    const range = scores.length >= 2 ? Math.max(...scores) - Math.min(...scores) : 0;
    const agreement: AxisConsensusRow["agreement"] = range <= 8 ? "strong" : range <= 18 ? "moderate" : "split";
    return { axis, personas, avgScore, agreement, heatLevel: Math.round(avgScore / 10) };
  });
}

export function buildArtistArchetypeProfile(review: DarkRoomReview): ArtistArchetypeProfile {
  const scores = review.compositeScores as Record<string, number>;
  const def = ARCHETYPE_DEFINITIONS.find((d) => d.match(scores)) ?? ARCHETYPE_DEFINITIONS[ARCHETYPE_DEFINITIONS.length - 1];
  return {
    archetype: def.archetype,
    tagline: def.tagline,
    strength: def.strength,
    growthEdge: def.growthEdge,
    nextMove: def.nextMove,
  };
}
